import { CookieService } from 'ngx-cookie-service';
import { Subject } from 'rxjs';
import { delay, filter, takeUntil } from 'rxjs/operators';

import {
	Component,
	HostListener,
	OnDestroy,
	OnInit,
	VERSION,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { TranslateService } from '@ngx-translate/core';

import { environment } from './core/environments/environment';
import { LoadingService } from './core/services/loading/loading.service';
import { UserPermissionsService } from './core/services/user-permissions/user-permissions.service';
import { Utils } from './shared/helper/Utils';

/** Class for the app component that is bootstrapped to run the application
 */
@Component({
    selector: 'body',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
	/** string used to hold the url for the skipToMain link */
	skipToMain: string;
	/** the Angular version */
	version = VERSION.full;
	/** whether we are production or not */
	envProd = environment.production;
	/** constructor for setting up DI in this component */
	displayDialog: boolean;
	fullMessage: boolean = true;
	year = Utils.getYear();

	loading: boolean = false;

	/** Subject used to signal when component is in ngOnDestroy */
	private readonly _destroying$ = new Subject<void>();

	@HostListener('window:resize', ['$event']) onresize(event?: {
		target: { innerWidth: number };
	}) {
		if (event.target.innerWidth > 767) {
			this.fullMessage = true;
		} else {
			this.fullMessage = false;
		}
	}

	constructor(
		private readonly router: Router,
		private cookieService: CookieService,
		private translateService: TranslateService,
		private _loading: LoadingService,
		private userPermissionsService: UserPermissionsService,
		private authService: MsalService,
		private msalBroadcastService: MsalBroadcastService,
	) {
		this.translateService.setDefaultLang('pt');

		if (this.cookieService.get('language')) {
			this.translateService.use(this.cookieService.get('language'));
		} else {
			this.translateService.use('pt');
			this.cookieService.set('language', 'pt');
		}
	}

	/** this class requires this method to implement the OnInit interface */
	ngOnInit() {
		this.listenToLoading();

		this.msalBroadcastService.inProgress$
			.pipe(
				filter(
					(status: InteractionStatus) =>
						status === InteractionStatus.None,
				),
				takeUntil(this._destroying$),
			)
			.subscribe(() => {
				this.checkAndSetActiveAccount();
				this.setUserInfoFromMsal();
				// Carrega as permissões após ter o userId disponível
				this.loadUserPermissions();
			});

		this.router.events
			.pipe(filter((event) => event instanceof NavigationEnd))
			.subscribe((event: NavigationEnd) => {
				this.setSkipLinkUrl(event.urlAfterRedirects);
			});
	}

	ngOnDestroy(): void {
		this._destroying$.next(undefined);
		this._destroying$.complete();
	}

	/**
	 * Checks if there is an active account set. If not, sets the first signed-in account as the active account.
	 * Note: Basic usage demonstrated. Your app may require more complicated account selection logic.
	 */
	checkAndSetActiveAccount() {
		const activeAccount = this.authService.instance.getActiveAccount();
		if (
			!activeAccount &&
			this.authService.instance.getAllAccounts().length > 0
		) {
			const accounts = this.authService.instance.getAllAccounts();
			this.authService.instance.setActiveAccount(accounts[0]);
		}
	}

	/**
	 * Extrai informações do usuário autenticado via MSAL e armazena no sessionStorage
	 */
	setUserInfoFromMsal() {
		const activeAccount = this.authService.instance.getActiveAccount();
		if (activeAccount) {
			const username =
				activeAccount.username?.split('@')[0] || activeAccount.name;
			const name = activeAccount.name;

			sessionStorage.setItem('username', username);
			sessionStorage.setItem('userId', username);
			sessionStorage.setItem('userFullName', name);

			this.authService
				.acquireTokenSilent({
					scopes: environment.apiConfig.scopes,
					account: activeAccount,
				})
				.subscribe({
					next: (result) => {
						const token = result.accessToken || result.idToken;
						sessionStorage.setItem('token', token);
						console.log('Token JWT armazenado com sucesso.');
					},
					error: (err) => {
						console.error('Erro ao adquirir token silenciosamente:', err);
						// fallback: usa o idToken da conta ativa se disponível
						const idToken = (activeAccount as any).idToken as string;
						if (idToken) {
							sessionStorage.setItem('token', idToken);
							console.warn('Usando idToken como fallback.');
						}
					},
				});
		}
	}

	/**
	 * Carrega as permissões do usuário após a autenticação
	 */
	loadUserPermissions() {
		this.userPermissionsService.getPermissions().subscribe(
			(result) => {
				let permissions = result;
				permissions = Array.from(new Set(permissions));
				sessionStorage.setItem(
					'permissions',
					JSON.stringify(permissions),
				);
				console.log('Permissões do usuário carregadas:', permissions);
			},
			(error) => {
				console.error('Erro ao carregar permissões:', error);
			},
		);
	}

	/**
	 * Listen to the loadingSub property in the LoadingService class. This drives the
	 * display of the loading spinner.
	 */
	listenToLoading(): void {
		this._loading.loadingSub
			.pipe(delay(0))
			.subscribe((loading) => {
				this.loading = loading;
			});
	}

	/**
	 * setSkipLinkUrl takes in a url string and processes whether
	 * the skipToMain link should be updated to use the new value
	 * @param currentUrl the new url to refer to
	 */
	private setSkipLinkUrl(currentUrl: string) {
		if (!currentUrl.endsWith('#app-content')) {
			this.skipToMain = `${currentUrl}#app-content`;
		}
	}

	showDialog() {
		this.displayDialog = true;
	}
}
